import { useEffect, useRef, useState } from 'react'
import './Vectorscope.css'

function Vectorscope({ videoRef, imageRef, useSMPTE = false, seekTime = 0, is2X = false, onToggle2X }) {
  const canvasRef = useRef(null)
  const containerRef = useRef(null)
  const animationFrameRef = useRef(null)
  const [isActive, setIsActive] = useState(true)
  const [brightness, setBrightness] = useState(15)
  const [zoom, setZoom] = useState(2.0) // 2x zoom by default
  const [showTargets, setShowTargets] = useState(true)
  const [showSkinTone, setShowSkinTone] = useState(true)
  const [canvasSize, setCanvasSize] = useState({ width: 0, height: 0 })
  const lastSeekTimeRef = useRef(seekTime)
  const lastFrameTimeRef = useRef(0)
  const tempCanvasRef = useRef(null)
  const TARGET_FPS = 30
  const FRAME_INTERVAL = 1000 / TARGET_FPS

  // Resize canvas to fit container
  useEffect(() => {
    const container = containerRef.current
    const canvas = canvasRef.current
    if (!container || !canvas) return

    const resizeCanvas = () => {
      const rect = container.getBoundingClientRect()
      const headerHeight = 60
      const availableHeight = rect.height - headerHeight
      const availableWidth = rect.width - 32
      
      // Make it square for circular vectorscope
      const size = Math.min(availableWidth, availableHeight)
      const newWidth = Math.floor(size)
      const newHeight = Math.floor(size)
      
      // Only update if size changed
      if (canvas.width !== newWidth || canvas.height !== newHeight) {
        canvas.width = newWidth
        canvas.height = newHeight
        setCanvasSize({ width: newWidth, height: newHeight })
      }
    }

    resizeCanvas()
    const resizeObserver = new ResizeObserver(resizeCanvas)
    resizeObserver.observe(container)

    return () => resizeObserver.disconnect()
  }, [])

  useEffect(() => {
    const canvas = canvasRef.current
    const video = useSMPTE ? null : videoRef.current
    const image = useSMPTE ? imageRef.current : null
    const source = useSMPTE ? image : video
    if (!canvas || !source) return

    if (!tempCanvasRef.current) {
      tempCanvasRef.current = document.createElement('canvas')
    }

    lastSeekTimeRef.current = seekTime

    const ctx = canvas.getContext('2d', {
      alpha: false,
      desynchronized: true,
      willReadFrequently: false
    })
    const width = canvas.width
    const height = canvas.height

    const drawVectorscope = (currentTime = performance.now()) => {
      const seekChanged = seekTime !== lastSeekTimeRef.current
      const isVideoReady = !useSMPTE && video && video.readyState >= 2 && !video.paused
      const isImageReady = useSMPTE && image && image.complete
      const shouldUpdate = isActive && (isVideoReady || isImageReady || seekChanged)

      const timeSinceLastFrame = currentTime - lastFrameTimeRef.current
      const shouldDraw = shouldUpdate && (seekChanged || timeSinceLastFrame >= FRAME_INTERVAL)

      if (!shouldDraw) {
        animationFrameRef.current = requestAnimationFrame(drawVectorscope)
        return
      }

      if (seekChanged) {
        lastSeekTimeRef.current = seekTime
      }
      lastFrameTimeRef.current = currentTime

      // Clear canvas
      ctx.fillStyle = '#000'
      ctx.fillRect(0, 0, width, height)

      // Get source dimensions
      const sourceWidth = useSMPTE ? (image.naturalWidth || 640) : (video.videoWidth || 640)
      const sourceHeight = useSMPTE ? (image.naturalHeight || 360) : (video.videoHeight || 360)

      const tempCanvas = tempCanvasRef.current
      if (tempCanvas.width !== sourceWidth || tempCanvas.height !== sourceHeight) {
        tempCanvas.width = sourceWidth
        tempCanvas.height = sourceHeight
      }

      const tempCtx = tempCanvas.getContext('2d', { willReadFrequently: false })
      tempCtx.drawImage(source, 0, 0, sourceWidth, sourceHeight)
      const imageData = tempCtx.getImageData(0, 0, sourceWidth, sourceHeight)
      const data = imageData.data

      // Vectorscope center and radius
      const centerX = width / 2
      const centerY = height / 2
      const maxRadius = Math.min(centerX, centerY) - 20
      const displayRadius = maxRadius / zoom // Apply zoom

      // Draw grid circles and lines
      ctx.strokeStyle = '#333'
      ctx.lineWidth = 1
      
      // Draw concentric circles at 25%, 50%, 75%, 100%
      for (let i = 1; i <= 4; i++) {
        const radius = (displayRadius * i) / 4
        ctx.beginPath()
        ctx.arc(centerX, centerY, radius, 0, Math.PI * 2)
        ctx.stroke()
      }

      // Draw crosshairs
      ctx.beginPath()
      ctx.moveTo(centerX - displayRadius, centerY)
      ctx.lineTo(centerX + displayRadius, centerY)
      ctx.moveTo(centerX, centerY - displayRadius)
      ctx.lineTo(centerX, centerY + displayRadius)
      ctx.stroke()

      // Helper function to convert RGB to YUV and calculate vectorscope position
      // This is used for both targets and skin tone line
      const rgbToVectorscopePos = (r, g, b) => {
        const rNorm = r / 255
        const gNorm = g / 255
        const bNorm = b / 255
        
        // Rec. 709 YUV conversion
        const Y = 0.2126 * rNorm + 0.7152 * gNorm + 0.0722 * bNorm
        const U = -0.1146 * rNorm - 0.3854 * gNorm + 0.5 * bNorm
        const V = 0.5 * rNorm - 0.4542 * gNorm - 0.0458 * bNorm
        
        // Calculate chroma magnitude and angle
        const chroma = Math.sqrt(U * U + V * V)
        const angle = Math.atan2(V, U)
        
        // Scale to display (same as pixel processing)
        const radius = chroma * 2 * displayRadius
        
        return { angle, radius, chroma }
      }

      // Draw targets at 75% saturation if enabled
      // Calculate actual positions from SMPTE color bar RGB values using Rec. 709 conversion
      if (showTargets) {
        const targetSize = 3
        
        // SMPTE color bars RGB values
        const smpteColors = [
          { r: 255, g: 0, b: 0, color: '#ff0000', label: 'R' },      // Red
          { r: 255, g: 255, b: 0, color: '#ffff00', label: 'Y' },    // Yellow
          { r: 0, g: 255, b: 0, color: '#00ff00', label: 'G' },     // Green
          { r: 0, g: 255, b: 255, color: '#00ffff', label: 'C' },   // Cyan
          { r: 0, g: 0, b: 255, color: '#0000ff', label: 'B' },     // Blue
          { r: 255, g: 0, b: 255, color: '#ff00ff', label: 'M' }    // Magenta
        ]
        
        // Calculate target positions at 75% of their actual saturation
        const targets = smpteColors.map(color => {
          const pos = rgbToVectorscopePos(color.r, color.g, color.b)
          // Scale to 75% saturation
          const targetRadius = pos.radius * 0.75
          return {
            ...color,
            angle: pos.angle,
            radius: targetRadius
          }
        })

        targets.forEach(target => {
          const x = centerX + Math.cos(target.angle) * target.radius
          const y = centerY - Math.sin(target.angle) * target.radius // Negative Y because canvas Y is inverted
          
          ctx.fillStyle = target.color
          ctx.beginPath()
          ctx.arc(x, y, targetSize, 0, Math.PI * 2)
          ctx.fill()
          
          // Draw target box
          ctx.strokeStyle = target.color
          ctx.lineWidth = 1
          ctx.strokeRect(x - targetSize - 1, y - targetSize - 1, (targetSize + 1) * 2, (targetSize + 1) * 2)
          
          // Draw label
          ctx.fillStyle = target.color
          ctx.font = '10px monospace'
          ctx.textAlign = 'center'
          ctx.fillText(target.label, x, y - targetSize - 5)
        })
      }

      // Draw skin tone line if enabled
      if (showSkinTone) {
        // Calculate skin tone line angle from actual skin tone RGB values
        // Using a representative skin tone (typical Caucasian skin: R=200, G=160, B=120)
        // This represents the average skin tone that should align with the line
        const skinToneR = 200
        const skinToneG = 160
        const skinToneB = 120
        
        const skinTonePos = rgbToVectorscopePos(skinToneR, skinToneG, skinToneB)
        const skinToneAngle = skinTonePos.angle
        
        // Draw line from center to edge at the calculated angle
        const skinToneX = centerX + Math.cos(skinToneAngle) * displayRadius
        const skinToneY = centerY - Math.sin(skinToneAngle) * displayRadius
        
        ctx.strokeStyle = '#ffaa00'
        ctx.lineWidth = 1.5
        ctx.setLineDash([4, 4])
        ctx.beginPath()
        ctx.moveTo(centerX, centerY)
        ctx.lineTo(skinToneX, skinToneY)
        ctx.stroke()
        ctx.setLineDash([])
      }

      // Process pixels and draw vectorscope data
      // Convert RGB to YUV (using Rec. 709)
      // Y = 0.2126*R + 0.7152*G + 0.0722*B
      // Cb = -0.1146*R - 0.3854*G + 0.5*B (normalized, then +128 for 8-bit)
      // Cr = 0.5*R - 0.4542*G - 0.0458*B (normalized, then +128 for 8-bit)
      // For vectorscope, we use U and V which are Cb-128 and Cr-128 (centered at 0)
      
      const sampleStep = Math.max(1, Math.floor(Math.max(sourceWidth, sourceHeight) / 200))
      
      // Create accumulation buffer for better visualization
      const bufferSize = Math.ceil(displayRadius * 2) + 2
      const accumulation = new Array(bufferSize).fill(0).map(() => 
        new Array(bufferSize).fill(0)
      )
      
      for (let y = 0; y < sourceHeight; y += sampleStep) {
        for (let x = 0; x < sourceWidth; x += sampleStep) {
          const idx = (y * sourceWidth + x) << 2
          
          if (idx + 2 < data.length) {
            const r = data[idx] / 255
            const g = data[idx + 1] / 255
            const b = data[idx + 2] / 255
            
            // Convert RGB to YUV (Rec. 709)
            const Y = 0.2126 * r + 0.7152 * g + 0.0722 * b
            // U (Cb) and V (Cr) normalized to -0.5 to 0.5 range
            const U = -0.1146 * r - 0.3854 * g + 0.5 * b
            const V = 0.5 * r - 0.4542 * g - 0.0458 * b
            
            // Calculate chroma magnitude and angle
            // Scale U and V to full range for display
            const chroma = Math.sqrt(U * U + V * V)
            const angle = Math.atan2(V, U)
            
            // Map to display coordinates
            // Scale chroma to fit display radius (max chroma is about 0.5, so scale by 2)
            const radius = chroma * 2 * displayRadius
            const scopeX = centerX + Math.cos(angle) * radius
            const scopeY = centerY - Math.sin(angle) * radius // Negative Y for canvas
            
            // Only draw if within display bounds
            if (radius <= displayRadius) {
              const accX = Math.floor(scopeX - centerX + displayRadius)
              const accY = Math.floor(scopeY - centerY + displayRadius)
              
              if (accX >= 0 && accX < bufferSize && accY >= 0 && accY < bufferSize) {
                accumulation[accY][accX]++
              }
            }
          }
        }
      }
      
      // Find max accumulation for normalization
      let maxAccum = 0
      for (let y = 0; y < accumulation.length; y++) {
        for (let x = 0; x < accumulation[y].length; x++) {
          if (accumulation[y][x] > maxAccum) {
            maxAccum = accumulation[y][x]
          }
        }
      }
      
      // Draw accumulated data
      const invMaxAccum = maxAccum > 0 ? 1 / maxAccum : 0
      ctx.fillStyle = '#ffffff'
      
      for (let y = 0; y < accumulation.length; y++) {
        for (let x = 0; x < accumulation[y].length; x++) {
          const count = accumulation[y][x]
          if (count > 0) {
            const alpha = Math.min(1, count * invMaxAccum * brightness)
            if (alpha > 0.01) {
              ctx.globalAlpha = alpha
              const px = x - displayRadius + centerX
              const py = y - displayRadius + centerY
              
              // Only draw if within circle
              const dx = px - centerX
              const dy = py - centerY
              const dist = Math.sqrt(dx * dx + dy * dy)
              if (dist <= displayRadius) {
                ctx.fillRect(px, py, 1, 1)
              }
            }
          }
        }
      }
      
      ctx.globalAlpha = 1

      // Draw labels
      ctx.fillStyle = '#888'
      ctx.font = '12px monospace'
      ctx.textAlign = 'center'
      
      // Draw I/Q labels (or U/V)
      const labelRadius = displayRadius + 15
      ctx.fillText('I+', centerX + labelRadius, centerY - 5)
      ctx.fillText('Q+', centerX + 5, centerY - labelRadius)
      ctx.fillText('I-', centerX - labelRadius, centerY - 5)
      ctx.fillText('Q-', centerX - 5, centerY + labelRadius)

      // Title
      ctx.fillStyle = '#fff'
      ctx.font = 'bold 14px monospace'
      ctx.textAlign = 'left'
      ctx.fillText('Vectorscope', 10, 20)

      animationFrameRef.current = requestAnimationFrame(drawVectorscope)
    }

    drawVectorscope()

    return () => {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current)
      }
    }
  }, [videoRef, imageRef, useSMPTE, isActive, seekTime, brightness, zoom, showTargets, showSkinTone, canvasSize])

  return (
    <div className="vectorscope" ref={containerRef}>
      <div className="vectorscope-header">
        <h2>Vectorscope</h2>
        <div className="header-controls">
          <label className="brightness-control">
            <span>Brightness:</span>
            <input
              type="range"
              min="0.1"
              max="20"
              step="0.1"
              value={brightness}
              onChange={(e) => setBrightness(parseFloat(e.target.value))}
              className="brightness-slider"
            />
            <span className="brightness-value">{brightness.toFixed(1)}</span>
          </label>
          <label className="zoom-control">
            <span>Zoom:</span>
            <input
              type="range"
              min="1"
              max="4"
              step="0.1"
              value={zoom}
              onChange={(e) => setZoom(parseFloat(e.target.value))}
              className="zoom-slider"
            />
            <span className="zoom-value">{zoom.toFixed(1)}x</span>
          </label>
          <button
            className={`toggle-button ${showTargets ? 'active' : ''}`}
            onClick={() => setShowTargets(!showTargets)}
            title="Toggle color targets"
          >
            TARGETS
          </button>
          <button
            className={`toggle-button ${showSkinTone ? 'active' : ''}`}
            onClick={() => setShowSkinTone(!showSkinTone)}
            title="Toggle skin tone line"
          >
            SKIN TONE
          </button>
          <button
            className="toggle-button"
            onClick={() => setIsActive(!isActive)}
          >
            {isActive ? 'Pause' : 'Resume'}
          </button>
          {onToggle2X && (
            <button
              className={`toggle-button ${is2X ? 'active' : ''}`}
              onClick={onToggle2X}
              title="2X Width (Desktop only)"
            >
              2X
            </button>
          )}
        </div>
      </div>
      <canvas
        ref={canvasRef}
        className="vectorscope-canvas"
      />
    </div>
  )
}

export default Vectorscope

